import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { Button, Typography } from '../../../../../../../components';
import { Modal } from '../../../modal/modal.component';
import { ModalIds } from '../../../../contexts/modals/modals.types';
import { VncScreen } from 'react-vnc';
import styles from './vnc-modal.module.scss';
import { useVNCModalHook } from './vnc-modal.hook';

/**
 * Bounded reconnect: passing our own `onDisconnect` disables react-vnc's built-in unconditional
 * auto-retry (which otherwise reconnects every `retryDuration` forever once a session existed —
 * a storm when the gateway/gluetun container is stopped mid-session). We allow a few silent retries
 * (covers a transient blip like a gateway restart), then surface an "offline" state with a manual Retry.
 */
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY_MS = 3000;

/**
 * VNCModal component
 * @description Content for the VNCModal window
 *
 * @author Sergii Goncharuk, Oleksii Medvediev
 * @category Components
 */
const VNCModal: FC = () => {
  const ref = useRef<React.ElementRef<typeof VncScreen>>(null);
  const { isValidUrl, vncUrl, vncPassword, vncUsername } = useVNCModalHook();

  const attemptsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [offline, setOffline] = useState(false);

  // A new url means a new session (e.g. modal reopened) — reset the counter and overlay, and always
  // clear a pending retry timer on unmount.
  useEffect(() => {
    attemptsRef.current = 0;
    setOffline(false);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [vncUrl]);

  const handleConnect = useCallback(() => {
    attemptsRef.current = 0;
    setOffline(false);
  }, []);

  const handleDisconnect = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (attemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
      attemptsRef.current += 1;
      timerRef.current = setTimeout(() => ref.current?.connect(), RECONNECT_DELAY_MS);
    } else {
      setOffline(true);
    }
  }, []);

  const retryManually = useCallback(() => {
    attemptsRef.current = 0;
    setOffline(false);
    ref.current?.connect();
  }, []);

  return (
    <Modal modalId={ModalIds.vnc} title={`IBKR Gateway (${vncUsername ?? '—'})`} className={styles.base}>
      <div className={styles.wrapper}>
        {vncUrl && vncPassword && isValidUrl ? (
          offline ? (
            <div className={styles.offline}>
              <Typography.Text variant={'bodyText3'}>IBKR Gateway offline.</Typography.Text>
              <Button variant={'main1'} size={'small'} label={'Retry'} onClick={retryManually} />
            </div>
          ) : (
            <VncScreen
              url={vncUrl}
              scaleViewport
              background="#0f0f11"
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onCredentialsRequired={(rfb) => {
                rfb?.sendCredentials({ password: vncPassword });
              }}
              className={styles.vnc}
              ref={ref}
            />
          )
        ) : (
          <Typography.Text variant={'bodyText3'}>Invalid VNC params..</Typography.Text>
        )}
      </div>
    </Modal>
  );
};

export { VNCModal };
